import { useMemo, useState } from 'react'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import { Localized } from '@lib/glossary/types'

const faqs: { q: Localized; a: Localized }[] = [
  {
    q: {
      en: 'How do I estimate peak concurrent sessions?',
      tr: 'Tepe eşzamanlı oturum sayısını nasıl tahmin ederim?',
    },
    a: {
      en: 'Look at how many privileged sessions actually run at the same time during your busiest window; most teams find it is 10-20% of their administrator count. Session-recording or VPN logs give you a real number in minutes; when in doubt, start at 15% of admins.',
      tr: 'En yoğun döneminizde aynı anda gerçekte kaç ayrıcalıklı oturumun çalıştığına bakın; çoğu ekipte bu, yönetici sayısının %10-20’sidir. Oturum kaydı veya VPN logları dakikalar içinde gerçek bir rakam verir; emin değilseniz yönetici sayısının %15’iyle başlayın.',
    },
  },
  {
    q: {
      en: 'Are the price assumptions real vendor prices?',
      tr: 'Fiyat varsayımları gerçek üretici fiyatları mı?',
    },
    a: {
      en: 'No. Enterprise PAM pricing is quote-based and varies widely, so the calculator ships with editable, illustrative defaults. Replace them with the numbers from your own quotes to compare your actual options.',
      tr: 'Hayır. Kurumsal PAM fiyatlandırması teklife dayalıdır ve büyük farklılık gösterir; bu yüzden hesaplayıcı düzenlenebilir, temsilî varsayılanlarla gelir. Gerçek seçeneklerinizi karşılaştırmak için bunları kendi tekliflerinizdeki rakamlarla değiştirin.',
    },
  },
  {
    q: {
      en: 'What costs does this calculator NOT include?',
      tr: 'Bu hesaplayıcı hangi maliyetleri içermez?',
    },
    a: {
      en: 'Professional-services fees, tiered support contracts, per-module add-ons, and infrastructure. These frequently add 30-100% to first-year PAM spend on per-user and per-asset models; ask every vendor to quote them explicitly.',
      tr: 'Profesyonel hizmet ücretleri, kademeli destek sözleşmeleri, modül başına ek ücretler ve altyapı. Bunlar kullanıcı ve varlık başına modellerde ilk yıl PAM harcamasına sıklıkla %30-100 ekler; her üreticiden bunları açıkça fiyatlandırmasını isteyin.',
    },
  },
  {
    q: {
      en: 'Why does per-concurrent-session licensing usually cost less?',
      tr: 'Eşzamanlı oturum başına lisanslama neden genellikle daha ucuz?',
    },
    a: {
      en: 'Because concurrency is a small fraction of headcount: 50 admins rarely run more than a dozen privileged sessions at once. Per-admin and per-asset models bill your org chart and server inventory; a session model bills actual privileged activity.',
      tr: 'Çünkü eşzamanlılık, toplam kişi sayısının küçük bir kısmıdır: 50 yönetici aynı anda nadiren bir düzineden fazla ayrıcalıklı oturum çalıştırır. Yönetici ve varlık başına modeller organizasyon şemanızı ve sunucu envanterinizi faturalandırır; oturum modeli gerçek ayrıcalıklı aktiviteyi faturalandırır.',
    },
  },
]

const ui = {
  seoTitle: {
    en: 'PAM TCO Calculator: Per-User vs Per-Asset vs Per-Session | Monofor',
    tr: 'PAM TCO Hesaplayıcı: Kullanıcı, Varlık ve Oturum Başına Karşılaştırma | Monofor',
  },
  seoDescription: {
    en: 'Free calculator: compare what privileged access management costs under per-admin, per-asset, and per-concurrent-session licensing, with your own numbers.',
    tr: 'Ücretsiz hesaplayıcı: ayrıcalıklı erişim yönetiminin yönetici başına, varlık başına ve eşzamanlı oturum başına lisanslamada neye mal olduğunu kendi rakamlarınızla karşılaştırın.',
  },
  jsonLdDescription: {
    en: 'Compare privileged access management licensing models: per-admin, per-asset, and per-concurrent-session.',
    tr: 'Ayrıcalıklı erişim yönetimi lisans modellerini karşılaştırın: yönetici başına, varlık başına ve eşzamanlı oturum başına.',
  },
  eyebrow: { en: 'Free tool', tr: 'Ücretsiz araç' },
  title: {
    en: 'What does PAM actually cost you?',
    tr: 'PAM size gerçekte neye mal oluyor?',
  },
  lede: {
    en: 'PAM vendors bill three different things: your admins, your servers, or your actual privileged sessions. Put your numbers in and see what each model costs per year.',
    tr: 'PAM üreticileri üç farklı şeyi faturalandırır: yöneticilerinizi, sunucularınızı veya gerçekte çalışan ayrıcalıklı oturumlarınızı. Rakamlarınızı girin, her modelin yıllık maliyetini görün.',
  },
  envHeading: { en: 'Your environment', tr: 'Ortamınız' },
  adminsLabel: {
    en: 'Privileged users (admins)',
    tr: 'Ayrıcalıklı kullanıcılar (yöneticiler)',
  },
  assetsLabel: {
    en: 'Servers / targets under management',
    tr: 'Yönetilen sunucular / hedefler',
  },
  sessionsLabel: {
    en: 'Peak concurrent privileged sessions',
    tr: 'Tepe eşzamanlı ayrıcalıklı oturum',
  },
  sessionsHint: {
    en: 'Typically 10-20% of your admin count',
    tr: 'Genellikle yönetici sayınızın %10-20’si',
  },
  assumptionsHeading: { en: 'Price assumptions', tr: 'Fiyat varsayımları' },
  assumptionsNote: {
    en: '(illustrative; replace with your quotes)',
    tr: '(temsilîdir; kendi tekliflerinizle değiştirin)',
  },
  perAdminPriceLabel: {
    en: 'Per-admin price ($/admin/month)',
    tr: 'Yönetici başına fiyat ($/yönetici/ay)',
  },
  perAssetPriceLabel: {
    en: 'Per-asset price ($/asset/month)',
    tr: 'Varlık başına fiyat ($/varlık/ay)',
  },
  perSessionPriceLabel: {
    en: 'Per-session price ($/concurrent session/month)',
    tr: 'Oturum başına fiyat ($/eşzamanlı oturum/ay)',
  },
  perAdminCard: { en: 'Per-admin licensing', tr: 'Yönetici başına lisanslama' },
  perAssetCard: { en: 'Per-asset licensing', tr: 'Varlık başına lisanslama' },
  adminsUnit: { en: 'admins', tr: 'yönetici' },
  assetsUnit: { en: 'assets', tr: 'varlık' },
  sessionsUnit: { en: 'concurrent sessions', tr: 'eşzamanlı oturum' },
  perSessionCard: {
    en: 'Per-concurrent-session licensing',
    tr: 'Eşzamanlı oturum başına lisanslama',
  },
  monopamModel: { en: 'the Monopam model', tr: 'Monopam modeli' },
  perYear: { en: 'per year', tr: 'yıllık' },
  threeYearTco: { en: '3-year TCO', tr: '3 yıllık TCO' },
  savingsBefore: { en: '~', tr: 'Bu varsayımlarla en ucuz yönetici/varlık başına seçeneğin ~%' },
  savingsAfter: {
    en: '% below the cheapest per-admin/per-asset option with these assumptions',
    tr: ' altında',
  },
  quoteCta: {
    en: 'Get an exact Monopam quote',
    tr: 'Net Monopam teklifi alın',
  },
  exploreCta: { en: 'Explore Monopam', tr: 'Monopam’ı keşfedin' },
  hiddenHeading: {
    en: 'Costs no license model shows you',
    tr: 'Hiçbir lisans modelinin göstermediği maliyetler',
  },
  hiddenItems: [
    {
      en: 'Professional services: legacy PAM deployments routinely require paid implementation engagements.',
      tr: 'Profesyonel hizmetler: eski nesil PAM kurulumları düzenli olarak ücretli implementasyon projeleri gerektirir.',
    },
    {
      en: 'Support tiers: ask whether support is included or a separate contract. (With Monofor it is included.)',
      tr: 'Destek kademeleri: desteğin dahil mi yoksa ayrı bir sözleşme mi olduğunu sorun. (Monofor’da dahildir.)',
    },
    {
      en: 'Module add-ons: session recording, JIT, or reporting sold separately add up at renewal.',
      tr: 'Modül ek ücretleri: ayrı satılan oturum kaydı, JIT veya raporlama yenileme zamanında birikir.',
    },
    {
      en: 'Agent rollout: endpoint agents mean deployment projects; browser-based access means none.',
      tr: 'Ajan dağıtımı: uç nokta ajanları dağıtım projesi demektir; tarayıcı tabanlı erişimde buna gerek yoktur.',
    },
  ] as Localized[],
  disclaimer: {
    en: 'All figures are estimates based on your inputs and editable assumptions; they are not vendor quotes. Actual pricing varies by volume, term, and negotiation.',
    tr: 'Tüm rakamlar girdilerinize ve düzenlenebilir varsayımlara dayalı tahminlerdir; üretici teklifleri değildir. Gerçek fiyatlar hacme, süreye ve pazarlığa göre değişir.',
  },
  faqEyebrow: { en: 'FAQ', tr: 'SSS' },
  faqHeading: { en: 'About the math.', tr: 'Hesabın mantığı.' },
}

function pick(v: Localized, locale: string) {
  return locale === 'tr' ? v.tr || v.en : v.en
}

type Props = { locale: string }

export default function PamTcoCalculatorPage({ locale }: Props) {
  const [admins, setAdmins] = useState(50)
  const [assets, setAssets] = useState(200)
  const [sessions, setSessions] = useState(8)
  const [perAdmin, setPerAdmin] = useState(55)
  const [perAsset, setPerAsset] = useState(8)
  const [perSession, setPerSession] = useState(120)

  const basePath = locale === 'tr' ? '/tr' : ''
  const p = (v: Localized) => pick(v, locale)

  const results = useMemo(() => {
    const perAdminAnnual = admins * perAdmin * 12
    const perAssetAnnual = assets * perAsset * 12
    const perSessionAnnual = sessions * perSession * 12
    const cheapestIncumbent = Math.min(perAdminAnnual, perAssetAnnual)
    const savings =
      cheapestIncumbent > 0
        ? Math.round((1 - perSessionAnnual / cheapestIncumbent) * 100)
        : 0
    return { perAdminAnnual, perAssetAnnual, perSessionAnnual, savings }
  }, [admins, assets, sessions, perAdmin, perAsset, perSession])

  const fmt = (n: number) => `$${n.toLocaleString('en-US')}`

  return (
    <Layout>
      <Seo
        title={p(ui.seoTitle)}
        description={p(ui.seoDescription)}
        path={`${basePath}/tools/pam-tco-calculator`}
      />
      <Head>
        <link
          rel="alternate"
          hrefLang="en"
          href={`${SITE_URL}/tools/pam-tco-calculator`}
        />
        <link
          rel="alternate"
          hrefLang="tr"
          href={`${SITE_URL}/tr/tools/pam-tco-calculator`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${SITE_URL}/tools/pam-tco-calculator`}
        />
      </Head>
      <JsonLd
        id="tool"
        data={{
          '@type': 'WebApplication',
          name: 'PAM TCO Calculator',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description: p(ui.jsonLdDescription),
          url: `${SITE_URL}${basePath}/tools/pam-tco-calculator`,
          publisher: { '@type': 'Organization', name: 'Monofor' },
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <JsonLd
        id="tool-faq"
        data={{
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: p(f.q),
            acceptedAnswer: { '@type': 'Answer', text: p(f.a) },
          })),
        }}
      />
      <main className="bg-surface text-ink-primary">
        {/* Hero */}
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
            <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
          </div>
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{p(ui.eyebrow)}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {p(ui.title)}
                </Heading>
              </div>
              <div className="mt-8 max-w-2xl">
                <Text size="xl" tone="secondary">
                  {p(ui.lede)}
                </Text>
              </div>
            </div>
          </Container>
        </Section>

        {/* Calculator */}
        <Section tone="subtle" density="default">
          <Container size="xl">
            <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-12">
              {/* Inputs */}
              <div className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card">
                <h2 className="text-display-sm font-medium text-ink-primary">
                  {p(ui.envHeading)}
                </h2>
                <div className="mt-6 space-y-6">
                  <SliderField
                    label={p(ui.adminsLabel)}
                    value={admins}
                    onChange={setAdmins}
                    min={5}
                    max={500}
                    step={5}
                  />
                  <SliderField
                    label={p(ui.assetsLabel)}
                    value={assets}
                    onChange={setAssets}
                    min={10}
                    max={2000}
                    step={10}
                  />
                  <SliderField
                    label={p(ui.sessionsLabel)}
                    hint={p(ui.sessionsHint)}
                    value={sessions}
                    onChange={setSessions}
                    min={1}
                    max={100}
                    step={1}
                  />
                </div>

                <h3 className="mt-10 text-body-lg font-medium text-ink-primary">
                  {p(ui.assumptionsHeading)}{' '}
                  <span className="text-body-sm font-normal text-ink-tertiary">
                    {p(ui.assumptionsNote)}
                  </span>
                </h3>
                <div className="mt-4 space-y-6">
                  <SliderField
                    label={p(ui.perAdminPriceLabel)}
                    value={perAdmin}
                    onChange={setPerAdmin}
                    min={10}
                    max={200}
                    step={5}
                  />
                  <SliderField
                    label={p(ui.perAssetPriceLabel)}
                    value={perAsset}
                    onChange={setPerAsset}
                    min={1}
                    max={50}
                    step={1}
                  />
                  <SliderField
                    label={p(ui.perSessionPriceLabel)}
                    value={perSession}
                    onChange={setPerSession}
                    min={20}
                    max={500}
                    step={10}
                  />
                </div>
              </div>

              {/* Results */}
              <div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <ResultCard
                    label={p(ui.perAdminCard)}
                    sub={`${admins} ${p(ui.adminsUnit)} × ${fmt(perAdmin)}/${
                      locale === 'tr' ? 'ay' : 'mo'
                    }`}
                    annual={fmt(results.perAdminAnnual)}
                    threeYear={fmt(results.perAdminAnnual * 3)}
                    perYearLabel={p(ui.perYear)}
                    threeYearLabel={p(ui.threeYearTco)}
                  />
                  <ResultCard
                    label={p(ui.perAssetCard)}
                    sub={`${assets} ${p(ui.assetsUnit)} × ${fmt(perAsset)}/${
                      locale === 'tr' ? 'ay' : 'mo'
                    }`}
                    annual={fmt(results.perAssetAnnual)}
                    threeYear={fmt(results.perAssetAnnual * 3)}
                    perYearLabel={p(ui.perYear)}
                    threeYearLabel={p(ui.threeYearTco)}
                  />
                </div>
                <div className="mt-4 rounded-3xl border border-brand-300 bg-surface p-8 shadow-card-hover ring-1 ring-brand-300">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-brand-700">
                      {p(ui.perSessionCard)}
                    </div>
                    <div className="text-caption text-ink-tertiary">
                      {p(ui.monopamModel)}
                    </div>
                  </div>
                  <div className="mt-2 text-body-sm text-ink-tertiary">
                    {sessions} {p(ui.sessionsUnit)} × {fmt(perSession)}/
                    {locale === 'tr' ? 'ay' : 'mo'}
                  </div>
                  <div className="mt-4 flex flex-wrap items-baseline gap-x-8 gap-y-2">
                    <div>
                      <div className="text-display-md font-medium text-ink-primary">
                        {fmt(results.perSessionAnnual)}
                      </div>
                      <div className="text-body-sm text-ink-tertiary">
                        {p(ui.perYear)}
                      </div>
                    </div>
                    <div>
                      <div className="text-display-sm font-medium text-ink-secondary">
                        {fmt(results.perSessionAnnual * 3)}
                      </div>
                      <div className="text-body-sm text-ink-tertiary">
                        {p(ui.threeYearTco)}
                      </div>
                    </div>
                  </div>
                  {results.savings > 0 && (
                    <div className="mt-5 inline-flex items-center gap-2 rounded-pill bg-accent-50 px-4 py-2 text-body-sm font-medium text-accent-800">
                      {p(ui.savingsBefore)}
                      {results.savings}
                      {p(ui.savingsAfter)}
                    </div>
                  )}
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Button href="/contact" variant="primary" size="md">
                      {p(ui.quoteCta)}
                    </Button>
                    <Button href="/pam" variant="outline" size="md">
                      {p(ui.exploreCta)}
                    </Button>
                  </div>
                </div>

                <div className="mt-6 rounded-2xl border border-line-subtle bg-surface p-6">
                  <div className="text-body-md font-medium text-ink-primary">
                    {p(ui.hiddenHeading)}
                  </div>
                  <ul className="mt-3 space-y-2 text-body-sm text-ink-secondary">
                    {ui.hiddenItems.map((item) => (
                      <li key={item.en}>{p(item)}</li>
                    ))}
                  </ul>
                </div>

                <div className="mt-4 text-body-sm text-ink-tertiary">
                  {p(ui.disclaimer)}
                </div>
              </div>
            </div>
          </Container>
        </Section>

        {/* FAQ */}
        <Section tone="default" density="default">
          <Container size="md">
            <Eyebrow tone="brand">{p(ui.faqEyebrow)}</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {p(ui.faqHeading)}
            </Heading>
            <dl className="mt-12 space-y-4">
              {faqs.map((faq) => (
                <div
                  key={faq.q.en}
                  className="rounded-2xl border border-line-subtle bg-surface-subtle p-6 lg:p-8"
                >
                  <dt className="text-body-lg font-medium text-ink-primary">
                    {p(faq.q)}
                  </dt>
                  <dd className="mt-3 text-body-md text-ink-secondary">
                    {p(faq.a)}
                  </dd>
                </div>
              ))}
            </dl>
          </Container>
        </Section>

        <FinalCta />
      </main>
    </Layout>
  )
}

function SliderField({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step,
}: {
  label: string
  hint?: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <label className="text-body-sm font-medium text-ink-primary">
          {label}
        </label>
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onChange(Number(e.target.value) || min)}
          className="w-24 rounded-lg border border-line bg-surface px-3 py-1.5 text-right text-body-sm text-ink-primary focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        />
      </div>
      {hint && <div className="mt-1 text-caption text-ink-tertiary">{hint}</div>}
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        className="mt-3 w-full accent-brand-700"
      />
    </div>
  )
}

function ResultCard({
  label,
  sub,
  annual,
  threeYear,
  perYearLabel,
  threeYearLabel,
}: {
  label: string
  sub: string
  annual: string
  threeYear: string
  perYearLabel: string
  threeYearLabel: string
}) {
  return (
    <div className="rounded-3xl border border-line-subtle bg-surface p-6 shadow-card">
      <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
        {label}
      </div>
      <div className="mt-2 text-body-sm text-ink-tertiary">{sub}</div>
      <div className="mt-4">
        <div className="text-display-sm font-medium text-ink-primary">{annual}</div>
        <div className="text-body-sm text-ink-tertiary">{perYearLabel}</div>
      </div>
      <div className="mt-3">
        <div className="text-body-lg font-medium text-ink-secondary">{threeYear}</div>
        <div className="text-body-sm text-ink-tertiary">{threeYearLabel}</div>
      </div>
    </div>
  )
}

export async function getStaticProps({ locale }: { locale?: string }) {
  return { props: { locale: locale ?? 'en' } }
}
